import { NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";

// Third Party
import { FormlyModule, FormlyFieldConfig } from "@ngx-formly/core";

// Local Components
import { SurveyComponent } from "./components/survey.component";
import { FormlyFieldSurveyReadOnlyComponent } from "./ng-formly/Components/survey-readonly.component";
import { FormlyFieldSurveyTextComponent } from "./ng-formly/Components/survey-text.component";
import { FormlyFieldSurveyRadioComponent } from "./ng-formly/Components/survey-radio.component";
import { FormlyFieldSurveyInputComponent } from "./ng-formly/Components/survey-input.component";
import { FormlyFieldSurveyPolicyProcedureComponent } from "./ng-formly/Components/survey-policy-procedure.component";
import { FormlyFieldSurveyMcssComponent } from "./ng-formly/Components/survey-mcss.component";
import { FormlyFieldSurveyMcssOtherComponent } from "./ng-formly/Components/survey-mcss-other.component";
import { FormlyFieldSurveyMcmsComponent } from "./ng-formly/Components/survey-mcms.component";
import { FormlyFieldSurveySelectComponent } from "./ng-formly/Components/survey-select.component";
import { FormlyFieldSurveyTextAreaComponent } from "./ng-formly/Components/survey-textarea.component";
import { StandardWrapperComponent } from "./ng-formly/Wrappers/standard-wrapper.component";
import { TextWrapperComponent } from "./ng-formly/Wrappers/text-wrapper.component";
import { PageWrapperComponent } from "./ng-formly/Wrappers/page-wrapper.component";
import { FormlyMaterialModule } from "@ngx-formly/material";
import { AppMatModule } from "../app-mat.module";
import { FormlyFieldSurveyDateComponent } from "./ng-formly/Components/survey-date.component";
import { TooltipModule } from "../components/tooltip/tooltip.module";
import { FormlyFieldSurveyHiddenComponent } from "./ng-formly/Components/survey-hidden.component";
import { FormlyFieldSurveyMcsshComponent } from "./ng-formly/Components/survey-mcssh.component";
import { RatingWrapperComponent } from "./ng-formly/Wrappers/rating-wrapper.component";
import { StudentSurveyWorkflowBarComponent } from "./components/workflow-bar/student-survey-workflow-bar/student-survey-workflow-bar.component";
import { SurveyWorkflowBarComponent } from "./components/workflow-bar/survey-workflow-bar/survey-workflow-bar.component";
import { FormlyFieldSurveyCommentComponent } from "./ng-formly/Components/survey-comment.component";
import { WorkflowDropdownComponent } from "./components/workflow-dropdown/workflow-dropdown.component";
import { LabelModule } from "@progress/kendo-angular-label";
import { DropDownsModule } from "@progress/kendo-angular-dropdowns";
import { InputsModule } from "@progress/kendo-angular-inputs";
import { LayoutModule } from "@progress/kendo-angular-layout";
import { IconsModule } from "@progress/kendo-angular-icons";
import { IndicatorsModule } from "@progress/kendo-angular-indicators";
import { FormlyFieldCommunicationSummaryComponent } from "./ng-formly/Components/survey-communication-summary.component";
import { CommunicationSummaryComponent } from "./components/communication-summary/communication-summary.component";
import { LeaMessagesComponent } from "./components/communication-summary/lea-messages/lea-messages.component";
import { InternalNotesComponent } from "./components/communication-summary/internal-notes/internal-notes.component";
import { SubmissionHistoryComponent } from "./components/communication-summary/submission-history/submission-history.component";
import { ActionRequiredCommentsComponent } from "./components/communication-summary/action-required-comments/action-required-comments.component";
import { ListViewModule } from "@progress/kendo-angular-listview";
import { MessageComponent } from "./components/communication-summary/shared/message/message.component";
import { SharedModule } from "../shared.module";

export function animationExtension(field: FormlyFieldConfig) {
  if (field.wrappers && field.wrappers.includes("animation")) {
    return;
  }

  field.wrappers = ["animation", ...(field.wrappers || [])];
}

@NgModule({
  declarations: [
    SurveyComponent,
    FormlyFieldSurveyDateComponent,
    FormlyFieldSurveyInputComponent,
    FormlyFieldSurveyReadOnlyComponent,
    FormlyFieldSurveyRadioComponent,
    FormlyFieldSurveyTextComponent,
    FormlyFieldSurveyPolicyProcedureComponent,
    FormlyFieldSurveyMcssComponent,
    FormlyFieldSurveyMcsshComponent,
    FormlyFieldSurveyMcssOtherComponent,
    FormlyFieldSurveyMcmsComponent,
    FormlyFieldSurveySelectComponent,
    FormlyFieldSurveyTextAreaComponent,
    FormlyFieldSurveyCommentComponent,
    FormlyFieldCommunicationSummaryComponent,
    TextWrapperComponent,
    RatingWrapperComponent,
    StandardWrapperComponent,
    PageWrapperComponent,
    StudentSurveyWorkflowBarComponent,
    SurveyWorkflowBarComponent,
    WorkflowDropdownComponent,
    CommunicationSummaryComponent,
    LeaMessagesComponent,
    InternalNotesComponent,
    SubmissionHistoryComponent,
    ActionRequiredCommentsComponent,
    MessageComponent,
  ],
  providers: [],
  exports: [
    SurveyComponent,
    StudentSurveyWorkflowBarComponent,
    SurveyWorkflowBarComponent,
    WorkflowDropdownComponent,
    CommunicationSummaryComponent,
    LeaMessagesComponent,
    InternalNotesComponent,
    SubmissionHistoryComponent,
    ActionRequiredCommentsComponent,
    MessageComponent,
  ],
  imports: [
    CommonModule,
    AppMatModule,
    TooltipModule,
    ReactiveFormsModule,
    FormsModule,
    FormlyModule.forRoot({
      extras: {
        resetFieldOnHide: false,
      },
      types: [
        {
          name: "surveydate",
          component: FormlyFieldSurveyDateComponent,
          wrappers: ["standard"],
        },
        {
          name: "surveytext",
          component: FormlyFieldSurveyTextComponent,
          wrappers: ["standard"],
        },
        {
          name: "surveyhidden",
          component: FormlyFieldSurveyHiddenComponent,
          wrappers: ["standard"],
        },
        {
          name: "surveyreadonly",
          component: FormlyFieldSurveyReadOnlyComponent,
          wrappers: ["text"],
        },
        {
          name: "surveyinput",
          component: FormlyFieldSurveyInputComponent,
          wrappers: ["standard"],
        },
        {
          name: "surveyradio",
          component: FormlyFieldSurveyRadioComponent,
          wrappers: ["standard"],
        },
        {
          name: "surveypolicyprocedure",
          component: FormlyFieldSurveyPolicyProcedureComponent,
          wrappers: ["standard"],
        },
        {
          name: "surveymcss",
          component: FormlyFieldSurveyMcssComponent,
          wrappers: ["standard"],
        },
        {
          name: "surveymcssh",
          component: FormlyFieldSurveyMcsshComponent,
          wrappers: ["rating"],
        },
        {
          name: "surveymcssother",
          component: FormlyFieldSurveyMcssOtherComponent,
          wrappers: ["standard"],
        },
        {
          name: "surveymcms",
          component: FormlyFieldSurveyMcmsComponent,
          wrappers: ["standard"],
        },
        {
          name: "surveyselect",
          component: FormlyFieldSurveySelectComponent,
          wrappers: ["standard"],
        },
        {
          name: "surveytextarea",
          component: FormlyFieldSurveyTextAreaComponent,
          wrappers: ["standard"],
        },
        {
          name: "surveycomment",
          component: FormlyFieldSurveyCommentComponent,
        },
        {
          name: "surveycommunicationsummary",
          component: FormlyFieldCommunicationSummaryComponent,
          wrappers: ["standard"],
        },
      ],
      wrappers: [
        { name: "text", component: TextWrapperComponent },
        { name: "standard", component: StandardWrapperComponent },
        { name: "rating", component: RatingWrapperComponent },
        { name: "page", component: PageWrapperComponent },
      ],
      /*validationMessages: [
              { name: "required", message: "This field is required" },
            ],*/
    }),
    FormlyMaterialModule,
    LabelModule,
    DropDownsModule,
    InputsModule,
    LayoutModule,
    IconsModule,
    IndicatorsModule,
    ListViewModule,
    SharedModule,
  ],
})
export class SurveyModule {}
